import React, {useEffect, useMemo, useState} from 'react';
import {Chip, CircularProgress, Stack, Typography} from '@mui/material';
import {getTagColor} from '../../../components/documentContent/tagColors';
import {suggestEntityTags} from '../../../firebase/firestore/sharedTags';

/**
 * LLM suggested tags strip for shared ICR / EEI / Gap / FINTEL / IIR / CT / CM tags.
 * Fetches automatically when mounted (same pattern as segment SuggestedTags).
 */
export default function SharedSuggestedTags({
    entityType,
    entityId,
    currentTags = [],
    onAdd,
    disabled = false,
    enabled = true,
}) {
    const [suggested, setSuggested] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!enabled || !entityType || !entityId) {
            setSuggested(null);
            setLoading(false);
            setError(null);
            return undefined;
        }
        let cancelled = false;
        setLoading(true);
        setError(null);
        suggestEntityTags(entityType, entityId)
            .then((data) => {
                if (!cancelled) setSuggested(data);
            })
            .catch((e) => {
                if (!cancelled) {
                    console.error('Shared tag suggestions failed:', e);
                    setSuggested({existingTags: [], newTags: []});
                    setError(e.message || 'Suggestion failed');
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [enabled, entityType, entityId]);

    const existingNames = useMemo(() => {
        const set = new Set();
        for (const t of currentTags || []) {
            const raw = typeof t === 'string' ? t : (t?.name || t?.tag || '');
            const n = String(raw).trim().toLowerCase();
            if (n) set.add(n);
        }
        return set;
    }, [currentTags]);

    const existing = useMemo(
        () => (suggested?.existingTags || []).filter((t) => {
            const label = (t?.name || '').toString().trim();
            return label && !existingNames.has(label.toLowerCase());
        }),
        [suggested, existingNames],
    );

    const novel = useMemo(
        () => (suggested?.newTags || []).filter((t) => {
            const label = (t?.name || '').toString().trim();
            return label && !existingNames.has(label.toLowerCase());
        }),
        [suggested, existingNames],
    );

    if (!enabled || !entityId) return null;

    return (
        <Stack spacing={0.5} sx={{mt: 0.5}}>
            {loading ? (
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="caption" fontStyle="italic" color="text.secondary">
                        Suggesting tags
                    </Typography>
                    <CircularProgress size="12px" disableShrink sx={{animationDuration: '375ms'}}/>
                </Stack>
            ) : error ? (
                <Typography variant="caption" color="error">{error}</Typography>
            ) : existing.length > 0 || novel.length > 0 ? (
                <>
                    <Typography variant="caption" color="text.secondary">Suggested tags</Typography>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        {[...existing, ...novel].map((t, i) => {
                            const label = (t?.name || '').toString();
                            if (!label) return null;
                            const color = getTagColor(t);
                            return (
                                <Chip
                                    key={`${i}-${label}`}
                                    label={label}
                                    size="small"
                                    variant="outlined"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (disabled || typeof onAdd !== 'function') return;
                                        onAdd(label);
                                    }}
                                    sx={{
                                        cursor: disabled ? 'not-allowed' : 'pointer',
                                        borderColor: color,
                                        color,
                                        '&:hover': {bgcolor: `${color}18`},
                                    }}
                                />
                            );
                        })}
                    </Stack>
                </>
            ) : (
                <Typography variant="caption" color="text.secondary">No suggested tags</Typography>
            )}
        </Stack>
    );
}
